import React, { useState } from 'react';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { CustomeTextField } from '../../../generic/CustomeTextField';
import { PostcodeSearchByID } from '../../../generic/PostcodeSearchById';
import { useUpdateAuthority } from '../../../Aemter/hooks';
import { useAlert } from '../../../context/AlertContext';
import { authoritySchema, IAuthority } from '../../../Interfaces/IAuthority';

interface IProps {
  currentAuthority: IAuthority;
  refetchAuthorities: () => void;
}

/** Stammdaten/Kontakt einer Behörde bearbeiten — analog zu EditCourt. */
export const EditAuthority: React.FC<IProps> = ({
  currentAuthority,
  refetchAuthorities,
}) => {
  const { showAlert } = useAlert();
  const [editAuthority, setEditAuthority] = useState(currentAuthority);
  const { updateAuthority, isPendingUpdateAuthority } = useUpdateAuthority();
  const isPending = isPendingUpdateAuthority;

  const handleReset = () => setEditAuthority(currentAuthority);

  const onUpdate = async () => {
    try {
      const updated = await updateAuthority(
        authoritySchema.parse(editAuthority)
      );
      setEditAuthority(updated);
      refetchAuthorities();
      showAlert({ text: 'Erfolgreich gespeichert!', severity: 'success' });
    } catch (error) {
      console.error(error);
      showAlert({ text: 'Es ist ein Fehler aufgetreten!', severity: 'error' });
    }
  };

  return (
    <>
      <Typography variant='h6'>Behörde</Typography>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12}>
          <CustomeTextField
            attr='Authority'
            object={editAuthority}
            label='Behörde'
            setObject={setEditAuthority}
            type='string'
            required
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={8}>
          <CustomeTextField
            attr='Street'
            object={editAuthority}
            label='Straße'
            setObject={setEditAuthority}
            type='string'
          />
        </Grid>
        <Grid item xs={4}>
          <CustomeTextField
            attr='StreetNr'
            object={editAuthority}
            label='Nr'
            setObject={setEditAuthority}
            type='string'
          />
        </Grid>
        <Grid item xs={12}>
          <PostcodeSearchByID
            key={`postcode-${editAuthority.idPostcode}`}
            idPostcode={editAuthority.idPostcode ?? null}
            setIdPostcode={(idPostcode: number) =>
              setEditAuthority({ ...editAuthority, idPostcode })
            }
            required={false}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={6}>
          <CustomeTextField
            attr='PhoneNumber'
            object={editAuthority}
            label='Telefonnummer'
            setObject={setEditAuthority}
            type='string'
          />
        </Grid>
        <Grid item xs={6}>
          <CustomeTextField
            attr='Authority_Email'
            object={editAuthority}
            label='E-Mail'
            setObject={setEditAuthority}
            type='string'
          />
        </Grid>
        <Grid item xs={12}>
          <CustomeTextField
            attr='Website'
            object={editAuthority}
            label='Website'
            setObject={setEditAuthority}
            type='string'
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant='outlined' onClick={handleReset} disabled={isPending}>
          Zurücksetzen
        </Button>
        <Button variant='contained' disabled={isPending} onClick={onUpdate}>
          {isPending ? (
            <Box display='flex' alignItems='center' gap={1}>
              <span>Speichern</span>
              <CircularProgress size={20} />
            </Box>
          ) : (
            'Speichern'
          )}
        </Button>
      </Box>
    </>
  );
};
